import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Users, FolderKanban, Send, Trash2, ShieldCheck, User as UserIcon, BarChart3, Building2, Save, Loader2, AlertTriangle } from 'lucide-react';
import { DeleteAccountDialog } from '@/components/DeleteAccountDialog';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/misc';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { InitialsAvatar } from '@/components/InitialsAvatar';
import { apiFetch } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { useMe } from '@/lib/auth';

interface Summary { total: number; sent: number; received: number; delivery_rate: number }
interface ProjUsage { project_id: string; name: string; numbers: number; total: number; sent: number; received: number }
interface AccountUsage { account: Summary; projects: ProjUsage[] }
interface AccUser { id: string; email: string; name: string; role: string; avatar_url?: string; email_verified_at?: string | null }

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <Card><CardContent className="p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-bold">{value}</p>
    </CardContent></Card>
  );
}

export function Account() {
  const t = useT();
  const qc = useQueryClient();
  const { data: me } = useMe(true);

  const { data: usage, isLoading: loadingUsage } = useQuery({
    queryKey: ['account-usage'],
    queryFn: async (): Promise<AccountUsage | null> => {
      const r = await apiFetch('GET', '/account/usage');
      return r.ok ? (r.data as AccountUsage) : null;
    },
  });
  const { data: users = [] } = useQuery({
    queryKey: ['account-users'],
    queryFn: async (): Promise<AccUser[]> => ((await apiFetch('GET', '/users')).data as { data?: AccUser[] })?.data ?? [],
  });

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('agent');
  const [inviting, setInviting] = useState(false);

  // Exclusão definitiva da conta (zona de perigo).
  const [delOpen, setDelOpen] = useState(false);
  const { data: delStatus } = useQuery({
    queryKey: ['account-deletion-status'],
    queryFn: async (): Promise<{ pending: boolean; run_after?: string }> => {
      const r = await apiFetch('GET', '/account/delete/status');
      return (r.ok ? r.data : { pending: false }) as { pending: boolean; run_after?: string };
    },
    refetchInterval: 30000,
    enabled: me?.role === 'admin',
  });
  const delMins = delStatus?.run_after ? Math.max(0, Math.round((new Date(delStatus.run_after).getTime() - Date.now()) / 60000)) : 0;
  const cancelDeletion = async () => {
    const r = await apiFetch('POST', '/account/delete/cancel');
    if (!r.ok) { toast.error(t('account.del.cancelFailed')); return; }
    toast.success(t('account.del.canceledToast'));
    qc.invalidateQueries({ queryKey: ['account-deletion-status'] });
  };

  // Nome da empresa (conta) — editável.
  const [company, setCompany] = useState('');
  const [savingCompany, setSavingCompany] = useState(false);
  useEffect(() => { if (me?.tenant_name != null) setCompany(me.tenant_name); }, [me?.tenant_name]);
  const saveCompany = async () => {
    if (!company.trim()) return;
    setSavingCompany(true);
    try {
      const r = await apiFetch('PATCH', '/account', { name: company.trim() });
      if (!r.ok) { toast.error(t('account.companySaveFailed')); return; }
      toast.success(t('account.companySaved'));
      qc.invalidateQueries({ queryKey: ['me'] }); // atualiza header, suporte (bFocus), etc.
    } finally { setSavingCompany(false); }
  };

  const invite = async () => {
    if (!email.trim()) return;
    setInviting(true);
    try {
      const r = await apiFetch('POST', '/users', { email: email.trim(), name: name.trim(), role });
      if (!r.ok) { toast.error(((r.data as { code?: string })?.code) === 'email_taken' ? t('account.emailTaken') : t('account.inviteFailed')); return; }
      toast.success(t('account.invited'));
      setEmail(''); setName('');
      qc.invalidateQueries({ queryKey: ['account-users'] });
    } finally { setInviting(false); }
  };
  const setUserRole = async (u: AccUser, r: string) => {
    const res = await apiFetch('PATCH', `/users/${u.id}`, { role: r });
    if (!res.ok) { toast.error(((res.data as { code?: string })?.code) === 'last_admin' ? t('account.lastAdmin') : t('account.updateFailed')); return; }
    qc.invalidateQueries({ queryKey: ['account-users'] });
  };
  const remove = async (u: AccUser) => {
    if (!window.confirm(t('account.removeConfirm').replace('{name}', u.name || u.email))) return;
    const res = await apiFetch('DELETE', `/users/${u.id}`);
    if (!res.ok) { toast.error(((res.data as { code?: string })?.code) === 'last_admin' ? t('account.lastAdmin') : t('account.removeFailed')); return; }
    qc.invalidateQueries({ queryKey: ['account-users'] });
  };

  const acc = usage?.account;

  return (
    <div className="flex flex-col gap-6">
      <p className="text-sm text-muted-foreground">{t('account.subtitle')}</p>

      {/* Nome da empresa (conta) — editável */}
      <Card>
        <CardContent className="flex flex-col gap-3 p-5 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex w-full flex-col gap-1.5">
            <Label className="flex items-center gap-2"><Building2 className="size-4 text-muted-foreground" /> {t('account.companyName')}</Label>
            <Input value={company} onChange={(e) => setCompany(e.target.value)} placeholder={t('account.companyPlaceholder')} />
            <p className="text-xs text-muted-foreground">{t('account.companyHint')}</p>
          </div>
          <Button className="w-full shrink-0 sm:w-auto" disabled={savingCompany || !company.trim() || company.trim() === (me?.tenant_name ?? '')} onClick={saveCompany}>
            {savingCompany ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />} {t('common.save')}
          </Button>
        </CardContent>
      </Card>

      {/* Consumo agregado da conta */}
      <div>
        <div className="mb-2 flex items-center gap-2"><BarChart3 className="size-4 text-muted-foreground" /><h2 className="text-base font-semibold">{t('account.consumption')}</h2><span className="text-xs text-muted-foreground">· {t('account.period30')}</span></div>
        {loadingUsage ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">{[0,1,2,3].map((i)=><Skeleton key={i} className="h-20 w-full" />)}</div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label={t('account.total')} value={acc?.total ?? 0} />
            <Stat label={t('account.sent')} value={acc?.sent ?? 0} />
            <Stat label={t('account.received')} value={acc?.received ?? 0} />
            <Stat label={t('account.deliveryRate')} value={`${Math.round((acc?.delivery_rate ?? 0) * 100)}%`} />
          </div>
        )}
      </div>

      {/* Consumo por projeto */}
      <Card>
        <CardContent className="overflow-x-auto p-0">
          <div className="flex items-center gap-2 border-b px-5 py-3"><FolderKanban className="size-4 text-muted-foreground" /><h2 className="text-sm font-semibold">{t('account.byProject')}</h2></div>
          <table className="w-full min-w-md text-sm">
            <thead><tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-5 py-2 font-medium">{t('account.project')}</th>
              <th className="px-5 py-2 font-medium">{t('account.numbers')}</th>
              <th className="px-5 py-2 font-medium">{t('account.sent')}</th>
              <th className="px-5 py-2 font-medium">{t('account.received')}</th>
              <th className="px-5 py-2 text-right font-medium">{t('account.total')}</th>
            </tr></thead>
            <tbody>
              {(usage?.projects ?? []).map((p) => (
                <tr key={p.project_id} className="border-b last:border-0">
                  <td className="px-5 py-2.5 font-medium">{p.name}</td>
                  <td className="px-5 py-2.5 text-muted-foreground">{p.numbers}</td>
                  <td className="px-5 py-2.5 text-muted-foreground">{p.sent}</td>
                  <td className="px-5 py-2.5 text-muted-foreground">{p.received}</td>
                  <td className="px-5 py-2.5 text-right font-semibold">{p.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Usuários da conta */}
      <Card>
        <CardContent className="flex flex-col gap-4 p-5">
          <div className="flex items-center gap-2"><Users className="size-4 text-muted-foreground" /><h2 className="text-base font-semibold">{t('account.team')}</h2></div>

          {/* Convidar */}
          <div className="flex flex-col gap-3 rounded-lg border bg-muted/30 p-3 sm:flex-row sm:items-end">
            <div className="flex-1"><Label>{t('account.inviteEmail')}</Label><Input value={email} onChange={(e)=>setEmail(e.target.value)} placeholder="colega@empresa.com" /></div>
            <div className="flex-1"><Label>{t('account.inviteName')}</Label><Input value={name} onChange={(e)=>setName(e.target.value)} placeholder="Nome" /></div>
            <div className="w-40"><Label>{t('account.role')}</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="agent">{t('account.member')}</SelectItem>
                  <SelectItem value="admin">{t('account.admin')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={invite} disabled={inviting || !email.trim()}><Send className="size-4" /> {t('account.invite')}</Button>
          </div>

          {/* Lista */}
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full min-w-md text-sm">
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b last:border-0">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <InitialsAvatar name={u.name} email={u.email} src={u.avatar_url} size={32} />
                        <div className="min-w-0">
                          <p className="truncate font-medium">{u.name || u.email}{me?.user_id === u.id && <span className="ml-1 text-xs text-muted-foreground">({t('account.you')})</span>}</p>
                          <p className="truncate text-xs text-muted-foreground">{u.email}{!u.email_verified_at && <span className="ml-1">· {t('account.pending')}</span>}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={u.role === 'admin' ? 'default' : 'secondary'} className="gap-1">
                        {u.role === 'admin' ? <ShieldCheck className="size-3" /> : <UserIcon className="size-3" />}
                        {u.role === 'admin' ? t('account.admin') : t('account.member')}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {me?.user_id !== u.id && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => setUserRole(u, u.role === 'admin' ? 'agent' : 'admin')}>
                              {u.role === 'admin' ? t('account.makeMember') : t('account.makeAdmin')}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => remove(u)}><Trash2 className="size-4 text-destructive" /></Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground">{t('account.roleHint')}</p>
        </CardContent>
      </Card>

      {/* Zona de perigo — exclusão definitiva da conta (só admin) */}
      {me?.role === 'admin' && (
        <Card className="border-destructive/40">
          <CardContent className="flex flex-col gap-3 p-5">
            <div className="flex items-center gap-2 text-destructive"><AlertTriangle className="size-4" /><h2 className="text-base font-semibold">{t('account.del.title')}</h2></div>
            {delStatus?.pending ? (
              <>
                <p className="text-sm font-medium">{t('account.del.scheduled')}</p>
                <p className="text-sm text-muted-foreground">{t('account.del.scheduledIn', { mins: delMins })}</p>
                <Button variant="outline" className="self-start" onClick={cancelDeletion}>{t('account.del.cancel')}</Button>
              </>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">{t('account.del.desc')}</p>
                <Button variant="destructive" className="self-start" onClick={() => setDelOpen(true)}><Trash2 className="size-4" /> {t('account.del.button')}</Button>
              </>
            )}
          </CardContent>
        </Card>
      )}
      <DeleteAccountDialog open={delOpen} onOpenChange={setDelOpen} base="/account/delete" onScheduled={() => qc.invalidateQueries({ queryKey: ['account-deletion-status'] })} />
    </div>
  );
}
